import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Card,
  Row,
  Col,
  Tag,
  Input,
  Select,
  Button,
  Space,
  Modal,
  Tooltip,
  Avatar,
  message,
  Statistic,
} from 'antd';
import {
  HistoryOutlined,
  SearchOutlined,
  ReloadOutlined,
  DownloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusCircleOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  SafetyCertificateOutlined,
  ClockCircleOutlined,
  UserOutlined,
  AppstoreOutlined,
  CloudUploadOutlined,
  PlayCircleOutlined,
  StopOutlined,
  FieldTimeOutlined,
  UploadOutlined,
  KeyOutlined,
  LockOutlined,
  CalculatorOutlined,
  SettingOutlined,
  ScissorOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { fetchAuditLogs, fetchAuditStats } from '../../../Service/Audit_API/auditApi';

dayjs.extend(relativeTime);

const { Option } = Select;

const ACTION_CONFIG = {
  CREATE: { color: 'green', icon: <PlusCircleOutlined />, label: 'Create' },
  SUBMIT: { color: 'cyan', icon: <CheckCircleOutlined />, label: 'Submit' },
  EDIT: { color: 'blue', icon: <EditOutlined />, label: 'Edit' },
  UPDATE: { color: 'geekblue', icon: <EditOutlined />, label: 'Update' },
  DELETE: { color: 'red', icon: <DeleteOutlined />, label: 'Delete' },
  COPY: { color: 'gold', icon: <CopyOutlined />, label: 'Copy' },
  ROLE_CHANGE: { color: 'purple', icon: <SafetyCertificateOutlined />, label: 'Role Change' },
  STATUS_CHANGE: { color: 'orange', icon: <ClockCircleOutlined />, label: 'Status Change' },
  LOGIN: { color: 'magenta', icon: <UserOutlined />, label: 'Login' },
  UPLOAD: { color: 'volcano', icon: <CloudUploadOutlined />, label: 'Upload' },
  BULK_UPLOAD: { color: 'cyan', icon: <UploadOutlined />, label: 'Bulk Upload' },
  BULK_CREATE: { color: 'green', icon: <PlusCircleOutlined />, label: 'Bulk Create' },
  BULK_DELETE: { color: 'red', icon: <DeleteOutlined />, label: 'Bulk Delete' },
  ACCESS_GRANT: { color: 'purple', icon: <KeyOutlined />, label: 'Grant Access' },
  ACCESS_REVOKE: { color: 'volcano', icon: <LockOutlined />, label: 'Revoke Access' },
  START: { color: 'green', icon: <PlayCircleOutlined />, label: 'Start Session' },
  END: { color: 'volcano', icon: <StopOutlined />, label: 'End Session' },
  EXTEND: { color: 'geekblue', icon: <FieldTimeOutlined />, label: 'Extend Session' },
  RECALCULATE: { color: 'blue', icon: <CalculatorOutlined />, label: 'Recalculate' },
  UPDATE_STRENGTH: { color: 'geekblue', icon: <SettingOutlined />, label: 'Strength Update' },
  TRIM: { color: 'blue', icon: <ScissorOutlined />, label: 'Trim Video' },
  CHAT: { color: 'cyan', icon: <MessageOutlined />, label: 'Chat' },
};

const MODULE_OPTIONS = [
  'RTMP Session',
  'Live Sessions',
  'Video Catalog',
  'Courses',
  'Modules',
  'Attendance',
  'Biometrics',
  'Accounts & Roles',
  'Candidates',
  'Instructors',
  'Staff',
  'Institutions',
  'Clusters',
  'Cluster Institutions',
  'Cluster Standards',
  'Academic Years',
  'Standards',
  'Subjects',
  'Lessons',
  'Daily Sessions',
  'Equipment',
  'Payments',
  'Assessments',
];

export default function AuditLogPage({ userRole = 'ADMIN' }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    creates: 0,
    edits: 0,
    deletes: 0,
    copies: 0,
    submits: 0,
    role_changes: 0,
    status_changes: 0
  });

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAction, setSelectedAction] = useState('ALL');
  const [selectedRole, setSelectedRole] = useState('ALL');
  const [selectedModule, setSelectedModule] = useState('ALL');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Details Modal
  const [selectedLog, setSelectedLog] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  // Load Statistics
  const loadStats = useCallback(async () => {
    try {
      const res = await fetchAuditStats();
      if (res && res.success && res.stats) {
        setStats(res.stats);
      }
    } catch (err) {
      console.warn('Failed to load audit stats:', err);
    }
  }, []);

  // Load Paginated Logs
  const loadLogs = useCallback(async (page = 1, size = 20) => {
    setLoading(true);
    try {
      const params = {
        page,
        pageSize: size,
        action_type: selectedAction !== 'ALL' ? selectedAction : undefined,
        user_role: selectedRole !== 'ALL' ? selectedRole : undefined,
        module_name: selectedModule !== 'ALL' ? selectedModule : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        search: searchTerm.trim() || undefined
      };
      const res = await fetchAuditLogs(params);
      if (res && res.success) {
        setLogs(res.logs || res.records || []);
        setTotalCount(res.total || 0);
        setCurrentPage(res.page || 1);
      } else {
        setLogs([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('Failed to load audit logs:', err);
      message.error(err.message || 'Failed to retrieve audit trail.');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [selectedAction, selectedRole, selectedModule, startDate, endDate, searchTerm]);

  useEffect(() => {
    loadStats();
    loadLogs(1, pageSize);
  }, [loadStats, loadLogs, pageSize]);

  // Reset all filters
  const handleResetFilters = () => {
    setSearchTerm('');
    setSelectedAction('ALL');
    setSelectedRole('ALL');
    setSelectedModule('ALL');
    setStartDate('');
    setEndDate('');
  };

  // Export filtered logs to CSV
  const handleExportCSV = () => {
    if (!logs || logs.length === 0) {
      message.warning('No audit logs to export.');
      return;
    }

    const headers = ['Log ID', 'Timestamp', 'User Name', 'Role', 'Action', 'Module', 'Target ID', 'Target Name', 'Description', 'IP Address', 'Status'];
    const rows = logs.map(l => [
      l.log_id,
      dayjs(l.created_at).format('YYYY-MM-DD HH:mm:ss'),
      `"${(l.user_name || '').replace(/"/g, '""')}"`,
      l.user_role || 'UNKNOWN',
      l.action_type,
      `"${(l.module_name || '').replace(/"/g, '""')}"`,
      `"${(l.target_id || '').replace(/"/g, '""')}"`,
      `"${(l.target_name || '').replace(/"/g, '""')}"`,
      `"${(l.description || '').replace(/"/g, '""')}"`,
      l.ip_address || '',
      l.status || 'SUCCESS'
    ]);

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `audit_logs_${dayjs().format('YYYY-MM-DD_HHmmss')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success('Audit logs exported successfully.');
  };

  const getActionBadge = (action) => {
    const actKey = (action || '').toUpperCase();
    const config = ACTION_CONFIG[actKey] || { color: 'default', icon: <HistoryOutlined />, label: action };
    return (
      <Tag color={config.color} icon={config.icon} style={{ fontWeight: 600, padding: '2px 8px', borderRadius: 4 }}>
        {config.label}
      </Tag>
    );
  };

  const getRoleTag = (role) => {
    const r = (role || '').toUpperCase();
    let color = 'default';
    if (r === 'ADMIN') color = '#7c3aed'; // Purple
    else if (r === 'STUDIO') color = '#0284c7'; // Cyan/Sky
    else if (r === 'USER') color = '#10b981'; // Emerald
    else if (r === 'AUTH') color = '#f59e0b'; // Amber

    return (
      <Tag style={{ background: `${color}15`, borderColor: `${color}40`, color, fontWeight: 600, fontSize: 11 }}>
        {role || 'UNKNOWN'}
      </Tag>
    );
  };

  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (date) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-color)' }}>
            {dayjs(date).format('DD/MM/YYYY hh:mm A')}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
            {dayjs(date).fromNow()}
          </div>
        </div>
      )
    },
    {
      title: 'User / Actor',
      key: 'user',
      width: 190,
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Avatar
            size="small"
            icon={<UserOutlined />}
            style={{
              background: record.user_role === 'ADMIN' ? '#7c3aed' : record.user_role === 'STUDIO' ? '#0284c7' : '#10b981'
            }}
          />
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-color)' }}>
              {record.user_name || 'System'}
            </div>
            <div>{getRoleTag(record.user_role)}</div>
          </div>
        </div>
      )
    },
    {
      title: 'Action',
      dataIndex: 'action_type',
      key: 'action_type',
      width: 140,
      align: 'center',
      render: (act) => getActionBadge(act)
    },
    {
      title: 'Module',
      dataIndex: 'module_name',
      key: 'module_name',
      width: 150,
      render: (mod) => (
        <Tag icon={<AppstoreOutlined />} style={{ borderRadius: 4, fontWeight: 500 }}>
          {mod || 'General'}
        </Tag>
      )
    },
    {
      title: 'Activity Description & Target',
      key: 'description',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-color)', lineHeight: 1.4 }}>
            {record.description || `${record.action_type} on ${record.module_name}`}
          </div>
          {(record.target_name || record.target_id) && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ color: '#64748b', fontWeight: 500 }}>Target:</span>
              {record.target_name && (
                <span style={{ color: 'var(--primary-color)', fontWeight: 600 }}>
                  {record.target_name}
                </span>
              )}
              {record.target_id && (
                <Tag style={{ fontSize: 10, padding: '0 4px', lineHeight: '16px', borderRadius: 3, background: 'rgba(99, 102, 241, 0.1)', borderColor: 'rgba(99, 102, 241, 0.25)', color: 'var(--primary-color)' }}>
                  ID #{record.target_id}
                </Tag>
              )}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'IP Address',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 130,
      render: (ip) => (
        <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)' }}>
          {ip ? (ip.startsWith('::ffff:') ? ip.replace('::ffff:', '') : ip) : '—'}
        </span>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (status) => (
        <Tag color={status === 'SUCCESS' ? 'success' : 'error'} style={{ fontWeight: 600 }}>
          {status || 'SUCCESS'}
        </Tag>
      )
    },
    {
      title: 'Details',
      key: 'actions',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Tooltip title="View Action Details">
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedLog(record);
              setIsDetailsModalOpen(true);
            }}
          />
        </Tooltip>
      )
    }
  ];

  return (
    <div style={{ padding: '4px 0', minHeight: '80vh' }}>
      {/* Page Header */}
      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text-color)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <HistoryOutlined style={{ color: 'var(--primary-color)' }} /> Audit Trail & Activity Logs
          </h2>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Comprehensive audit tracking for all role updates, edits, deletes, copies, and form submissions across the system.
          </span>
        </div>
        <Space wrap>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              loadStats();
              loadLogs(currentPage, pageSize);
            }}
            loading={loading}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleExportCSV}
            style={{ background: '#10b981', borderColor: '#10b981' }}
          >
            Export CSV
          </Button>
        </Space>
      </div>

      {/* KPI Stats Cards */}
      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        <Col xs={12} sm={8} md={4}>
          <Card bodyStyle={{ padding: 14 }} style={{ background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8 }}>
            <Statistic
              title={<span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Total Actions</span>}
              value={stats.total || totalCount}
              prefix={<HistoryOutlined style={{ color: 'var(--primary-color)' }} />}
              valueStyle={{ fontSize: 20, fontWeight: 700, color: 'var(--text-color)' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card bodyStyle={{ padding: 14 }} style={{ background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8 }}>
            <Statistic
              title={<span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Edits & Updates</span>}
              value={stats.edits}
              prefix={<EditOutlined style={{ color: '#3b82f6' }} />}
              valueStyle={{ fontSize: 20, fontWeight: 700, color: '#3b82f6' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card bodyStyle={{ padding: 14 }} style={{ background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8 }}>
            <Statistic
              title={<span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Deletions</span>}
              value={stats.deletes}
              prefix={<DeleteOutlined style={{ color: '#ef4444' }} />}
              valueStyle={{ fontSize: 20, fontWeight: 700, color: '#ef4444' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card bodyStyle={{ padding: 14 }} style={{ background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8 }}>
            <Statistic
              title={<span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Copies</span>}
              value={stats.copies}
              prefix={<CopyOutlined style={{ color: '#f59e0b' }} />}
              valueStyle={{ fontSize: 20, fontWeight: 700, color: '#f59e0b' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card bodyStyle={{ padding: 14 }} style={{ background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8 }}>
            <Statistic
              title={<span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Submissions</span>}
              value={stats.submits + stats.creates}
              prefix={<CheckCircleOutlined style={{ color: '#10b981' }} />}
              valueStyle={{ fontSize: 20, fontWeight: 700, color: '#10b981' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card bodyStyle={{ padding: 14 }} style={{ background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8 }}>
            <Statistic
              title={<span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Role / Status Changes</span>}
              value={stats.role_changes + stats.status_changes}
              prefix={<SafetyCertificateOutlined style={{ color: '#8b5cf6' }} />}
              valueStyle={{ fontSize: 20, fontWeight: 700, color: '#8b5cf6' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filter & Search Bar */}
      <Card
        bodyStyle={{ padding: 16 }}
        style={{
          background: 'var(--panel-color)',
          borderColor: 'var(--border-color)',
          borderRadius: 8,
          marginBottom: 16
        }}
      >
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Search user, target, IP, description..."
              prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Select
              style={{ width: '100%' }}
              value={selectedAction}
              onChange={val => setSelectedAction(val)}
              placeholder="Action Type"
              showSearch
            >
              <Option value="ALL">All Actions</Option>
              <Option value="CREATE">Create</Option>
              <Option value="EDIT">Edit</Option>
              <Option value="UPDATE">Update</Option>
              <Option value="DELETE">Delete</Option>
              <Option value="COPY">Copy</Option>
              <Option value="SUBMIT">Submit</Option>
              <Option value="ROLE_CHANGE">Role Change</Option>
              <Option value="STATUS_CHANGE">Status Change</Option>
              <Option value="LOGIN">Login</Option>
              <Option value="UPLOAD">Upload</Option>
              <Option value="BULK_UPLOAD">Bulk Upload</Option>
              <Option value="BULK_CREATE">Bulk Create</Option>
              <Option value="BULK_DELETE">Bulk Delete</Option>
              <Option value="ACCESS_GRANT">Grant Access</Option>
              <Option value="ACCESS_REVOKE">Revoke Access</Option>
              <Option value="START">Start Session</Option>
              <Option value="END">End Session</Option>
              <Option value="EXTEND">Extend Session</Option>
              <Option value="RECALCULATE">Recalculate</Option>
            </Select>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Select
              style={{ width: '100%' }}
              value={selectedRole}
              onChange={val => setSelectedRole(val)}
              placeholder="User Role"
            >
              <Option value="ALL">All Roles</Option>
              <Option value="ADMIN">ADMIN</Option>
              <Option value="STUDIO">STUDIO</Option>
              <Option value="USER">USER</Option>
              <Option value="AUTH">AUTH</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              style={{ width: '100%' }}
              value={selectedModule}
              onChange={val => setSelectedModule(val)}
              placeholder="Module"
              showSearch
            >
              <Option value="ALL">All Modules</Option>
              {MODULE_OPTIONS.map(m => (
                <Option key={m} value={m}>{m}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Input
              type="date"
              placeholder="From Date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Input
              type="date"
              placeholder="To Date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={2}>
            {(searchTerm || selectedAction !== 'ALL' || selectedRole !== 'ALL' || selectedModule !== 'ALL' || startDate || endDate) && (
              <Button type="link" onClick={handleResetFilters} style={{ padding: 0 }}>
                Reset Filters
              </Button>
            )}
          </Col>
        </Row>
      </Card>

      {/* Main Table */}
      <Card
        bodyStyle={{ padding: 0 }}
        style={{
          background: 'var(--panel-color)',
          borderColor: 'var(--border-color)',
          borderRadius: 8,
          overflow: 'hidden'
        }}
      >
        <Table
          dataSource={logs}
          columns={columns}
          loading={loading}
          rowKey="log_id"
          size="middle"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalCount,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} logs`,
            onChange: (p, s) => {
              setPageSize(s);
              loadLogs(p, s);
            }
          }}
        />
      </Card>

      {/* Details Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <HistoryOutlined style={{ color: 'var(--primary-color)' }} />
            <span>Audit Action Details #{selectedLog?.log_id}</span>
          </div>
        }
        open={isDetailsModalOpen}
        onCancel={() => {
          setIsDetailsModalOpen(false);
          setSelectedLog(null);
        }}
        footer={[
          <Button key="close" type="primary" onClick={() => setIsDetailsModalOpen(false)}>
            Close
          </Button>
        ]}
        width={650}
        centered
      >
        {selectedLog && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Row gutter={[12, 12]}>
              <Col span={12}>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Actor / User:</div>
                <div style={{ fontWeight: 600, fontSize: 14, marginTop: 2 }}>
                  {selectedLog.user_name} ({getRoleTag(selectedLog.user_role)})
                </div>
              </Col>
              <Col span={12}>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Timestamp:</div>
                <div style={{ fontWeight: 500, marginTop: 2 }}>
                  {dayjs(selectedLog.created_at).format('DD/MM/YYYY hh:mm:ss A')} ({dayjs(selectedLog.created_at).fromNow()})
                </div>
              </Col>
            </Row>

            <Row gutter={[12, 12]}>
              <Col span={12}>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Action Type:</div>
                <div style={{ marginTop: 2 }}>{getActionBadge(selectedLog.action_type)}</div>
              </Col>
              <Col span={12}>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Module:</div>
                <div style={{ fontWeight: 600, marginTop: 2 }}>{selectedLog.module_name}</div>
              </Col>
            </Row>

            {(selectedLog.target_name || selectedLog.target_id) && (
              <Row gutter={[12, 12]}>
                <Col span={12}>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Target Item:</div>
                  <div style={{ fontWeight: 600, marginTop: 2 }}>{selectedLog.target_name || 'N/A'}</div>
                </Col>
                <Col span={12}>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Target ID:</div>
                  <div style={{ fontFamily: 'monospace', fontWeight: 600, marginTop: 2 }}>#{selectedLog.target_id || 'N/A'}</div>
                </Col>
              </Row>
            )}

            <div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Description:</div>
              <div style={{ background: 'rgba(15, 23, 42, 0.05)', padding: '8px 12px', borderRadius: 6, marginTop: 4, fontSize: 13 }}>
                {selectedLog.description}
              </div>
            </div>

            <Row gutter={[12, 12]}>
              <Col span={12}>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Client IP:</div>
                <div style={{ fontFamily: 'monospace', fontSize: 13, marginTop: 2 }}>{selectedLog.ip_address || 'Unknown'}</div>
              </Col>
              <Col span={12}>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Status:</div>
                <div style={{ marginTop: 2 }}>
                  <Tag color={selectedLog.status === 'SUCCESS' ? 'success' : 'error'}>
                    {selectedLog.status}
                  </Tag>
                </div>
              </Col>
            </Row>

            {selectedLog.details && (
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>Payload / Parameters Details:</div>
                <pre
                  style={{
                    background: '#0f172a',
                    color: '#38bdf8',
                    padding: 12,
                    borderRadius: 6,
                    maxHeight: 220,
                    overflowY: 'auto',
                    fontSize: 12,
                    fontFamily: 'monospace'
                  }}
                >
                  {typeof selectedLog.details === 'string'
                    ? selectedLog.details
                    : JSON.stringify(selectedLog.details, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
